import { useState } from "react";
import { Plus, Trash2, Loader2, Save, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EMPTY_ITEM = { exercise_id: "", sets: 3, reps: "10", weekly_frequency: 3, rest_time: "60s" };
const newItem = () => ({ ...EMPTY_ITEM, _uid: Math.random().toString(36).slice(2) });

export const ProgramForm = ({ initial = {}, exercises, subscribers = [], onClose, onSaved }) => {
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    patient_email: initial.patient_email || "",
    patient_name: initial.patient_name || "",
    notes: initial.notes || "",
    items:
      initial.items && initial.items.length > 0
        ? initial.items.map(({ exercise_id, sets, reps, weekly_frequency, rest_time }) => ({
            exercise_id, sets, reps, weekly_frequency, rest_time,
            _uid: Math.random().toString(36).slice(2),
          }))
        : [newItem()],
  });

  const updateItem = (index, field, value) => {
    const items = [...form.items];
    items[index] = { ...items[index], [field]: value };
    setForm({ ...form, items });
  };

  const handleSave = async (e) => {
    e.preventDefault();
    const validItems = form.items.filter((i) => i.exercise_id);
    if (validItems.length === 0) {
      toast.error("Aggiungi almeno un esercizio");
      return;
    }
    setLoading(true);
    try {
      await axios.post(
        `${API}/programs`,
        {
          ...form,
          items: validItems.map((i) => ({
            exercise_id: i.exercise_id,
            reps: i.reps,
            rest_time: i.rest_time,
            sets: parseInt(i.sets, 10) || 1,
            weekly_frequency: parseInt(i.weekly_frequency, 10) || 1,
          })),
        },
        { withCredentials: true }
      );
      toast.success("Scheda salvata");
      onSaved();
    } catch (err) {
      toast.error("Errore nel salvataggio della scheda");
    } finally {
      setLoading(false);
    }
  };

  const inputClass =
    "w-full px-3 py-2.5 rounded-xl border border-stone-200 focus:outline-none focus:ring-2 focus:ring-[#2c4c3b]/20 text-sm";

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-3xl p-6 sm:p-8 max-w-3xl w-full max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-6">
          <h3 className="font-serif text-2xl text-stone-900">Scheda Esercizi</h3>
          <button onClick={onClose} className="p-2 hover:bg-stone-100 rounded-full">
            <X className="w-5 h-5 text-stone-500" />
          </button>
        </div>
        <form onSubmit={handleSave} className="space-y-5">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm text-stone-600 mb-2">Email Paziente *</label>
              <input
                type="email"
                value={form.patient_email}
                onChange={(e) => {
                  const email = e.target.value;
                  const sub = subscribers.find((s) => s.email === email);
                  setForm({ ...form, patient_email: email, patient_name: sub ? sub.name : form.patient_name });
                }}
                required
                list="subscriber-emails"
                className={inputClass}
                data-testid="program-email-input"
              />
              <datalist id="subscriber-emails">
                {subscribers.map((s) => (
                  <option key={s.id} value={s.email}>{s.name}</option>
                ))}
              </datalist>
            </div>
            <div>
              <label className="block text-sm text-stone-600 mb-2">Nome Paziente *</label>
              <input type="text" value={form.patient_name} onChange={(e) => setForm({ ...form, patient_name: e.target.value })} required className={inputClass} data-testid="program-name-input" />
            </div>
          </div>

          <div>
            <div className="flex items-center justify-between mb-3">
              <label className="text-sm font-medium text-stone-700">Esercizi</label>
              <button
                type="button"
                onClick={() => setForm({ ...form, items: [...form.items, newItem()] })}
                className="text-sm text-[#2c4c3b] font-medium hover:underline flex items-center gap-1"
                data-testid="add-program-item-btn"
              >
                <Plus className="w-4 h-4" /> Aggiungi esercizio
              </button>
            </div>
            <div className="space-y-4">
              {form.items.map((item, index) => (
                <div key={item._uid} className="bg-[#f5f2ec] rounded-2xl p-4 space-y-3">
                  <div className="flex items-center gap-2">
                    <select
                      value={item.exercise_id}
                      onChange={(e) => updateItem(index, "exercise_id", e.target.value)}
                      required
                      className={`${inputClass} flex-1 bg-white`}
                      data-testid={`program-item-exercise-${index}`}
                    >
                      <option value="">Seleziona esercizio...</option>
                      {exercises.map((ex) => {
                        const optionLabel = `${ex.code} — ${ex.title}`;
                        return (
                          <option key={ex.id} value={ex.id}>{optionLabel}</option>
                        );
                      })}
                    </select>
                    <button
                      type="button"
                      onClick={() => setForm({ ...form, items: form.items.filter((_, i) => i !== index) })}
                      className="p-2 text-stone-400 hover:text-red-500 rounded-lg"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                    <div>
                      <label className="block text-xs text-stone-500 mb-1">Serie</label>
                      <input type="number" min="1" value={item.sets} onChange={(e) => updateItem(index, "sets", e.target.value)} className={`${inputClass} bg-white`} />
                    </div>
                    <div>
                      <label className="block text-xs text-stone-500 mb-1">Ripetizioni</label>
                      <input type="text" value={item.reps} onChange={(e) => updateItem(index, "reps", e.target.value)} placeholder="10 o 30 sec" className={`${inputClass} bg-white`} />
                    </div>
                    <div>
                      <label className="block text-xs text-stone-500 mb-1">Freq. settimanale</label>
                      <input type="number" min="1" max="7" value={item.weekly_frequency} onChange={(e) => updateItem(index, "weekly_frequency", e.target.value)} className={`${inputClass} bg-white`} />
                    </div>
                    <div>
                      <label className="block text-xs text-stone-500 mb-1">Recupero</label>
                      <input type="text" value={item.rest_time} onChange={(e) => updateItem(index, "rest_time", e.target.value)} placeholder="60s" className={`${inputClass} bg-white`} />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div>
            <label className="block text-sm text-stone-600 mb-2">Note per il paziente</label>
            <textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={2} className={`${inputClass} resize-none`} data-testid="program-notes-input" />
          </div>

          <div className="flex gap-4 pt-2">
            <Button type="button" variant="outline" onClick={onClose} className="flex-1 rounded-full py-6">
              Annulla
            </Button>
            <Button type="submit" disabled={loading} className="flex-1 bg-[#2c4c3b] hover:bg-[#1a2e22] text-stone-50 rounded-full py-6" data-testid="save-program-btn">
              {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : (<><Save className="w-4 h-4 mr-2" />Salva Scheda</>)}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ProgramForm;
